/**
 * ParameterGroupItem - Collapsible group of parameter sources in the sidebar.
 *
 * When a parameter has multiple sources (e.g., alkalinity from Mastertronic,
 * AquaWiz, and Apex), this component renders them as a collapsible group
 * showing just the source names.
 */
import { Accordion, Group, Switch, Text, Box } from '@mantine/core';
import { getParameterConfig, parseParameterKey, getSourceDisplayName } from './constants';

interface ParameterGroupItemProps {
  /** Base parameter name (e.g., "alkalinity") */
  param: string;
  /** All composite keys for this parameter */
  keys: string[];
  /** Currently selected keys */
  selectedKeys: string[];
  /** Callback when a key is toggled */
  onToggle: (key: string, checked: boolean) => void;
  /** Whether to start expanded (for testing) */
  defaultExpanded?: boolean;
}

export function ParameterGroupItem({
  param,
  keys,
  selectedKeys,
  onToggle,
  defaultExpanded = false,
}: ParameterGroupItemProps): JSX.Element {
  const config = getParameterConfig(param);
  const selectedCount = keys.filter((k) => selectedKeys.includes(k)).length;

  // Format header: "Alkalinity (2)" or "Alkalinity (1/2)" if some selected
  const headerText =
    selectedCount > 0
      ? `${config.label} (${selectedCount}/${keys.length})`
      : `${config.label} (${keys.length})`;

  return (
    <Accordion
      variant="contained"
      defaultValue={defaultExpanded ? param : undefined}
      styles={{
        control: { padding: '4px 8px' },
        content: { padding: '0 8px 8px 24px' },
      }}
    >
      <Accordion.Item value={param}>
        <Accordion.Control>
          <Text size="sm">{headerText}</Text>
        </Accordion.Control>
        <Accordion.Panel>
          <Box>
            {keys.map((key) => {
              const { source } = parseParameterKey(key);
              // Use source name, or param label if no source
              const label = source ? getSourceDisplayName(source) : config.label;
              const isChecked = selectedKeys.includes(key);

              return (
                <Group key={key} justify="space-between" py={4}>
                  <Text size="sm">{label}</Text>
                  <Switch
                    checked={isChecked}
                    onChange={(event) => onToggle(key, event.currentTarget.checked)}
                    aria-label={label}
                    size="sm"
                  />
                </Group>
              );
            })}
          </Box>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}
